import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import * as Sentry from '@sentry/browser';
import { baseApi, getErrorMessage } from '@/shared/api';
import { legacyFiltersValueAdapter } from '@/shared/data';
import type { SharedPreset } from '../model/types';
import type { SharedPresetDTO } from './types';

export const api = createApi({
  reducerPath: 'sharedPresetsAPI',
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    getSharedPreset: builder.query({
      queryFn: async (id: string) => {
        const { data, error } = await baseApi
          .from('shared_views')
          .select('*')
          .eq('id', id)
          .returns<SharedPresetDTO[]>();

        if (!data) {
          Sentry.captureException(error);

          return { error: new Error(getErrorMessage(error)) };
        }

        return {
          data: {
            ...data[0].view,
            organizationId: data[0].organization_id,
            snapshot: {
              ...(data[0].view.snapshot as Object),
              filters: legacyFiltersValueAdapter(
                (
                  data[0].view.snapshot as {
                    filters: { id: string; value: unknown }[];
                  }
                ).filters,
              ),
            },
          },
        };
      },
    }),
    createSharedPreset: builder.mutation<string, { preset: SharedPreset, organizationId: string }>({
      queryFn: async ({ preset, organizationId }) => {
        const { data, error } = await baseApi
          .from('shared_views')
          .insert([
            {
              view: preset,
              organization_id: organizationId,
            },
          ])
          .select()
          .returns<[SharedPresetDTO]>();

        if (!data) {
          Sentry.captureException(error);

          return { error: new Error(getErrorMessage(error)) };
        }

        return { data: data[0].id };
      },
    }),
  }),
});

export const {
  useGetSharedPresetQuery,
  useCreateSharedPresetMutation,
  middleware,
} = api;
